/* ----------------------------------------------------------------------------
 * Social Connections — data (fetch) layer. Thin, typed, workspace-scoped.
 *
 * Workspace scoping is SERVER-authoritative: every call rides the cookie session
 * (credentials:'include') and the API derives the workspace from it — the same
 * model as the rest of src/data/*.ts. The state-changing assign-domain POST
 * carries the CSRF double-submit header automatically (lib/api reads the bc_csrf
 * cookie and echoes X-CSRF-Token on every non-GET; nothing to attach by hand).
 *
 * Authorised endpoints (Lane B, LOCKED contract — see social-connections-model.ts):
 *   GET  /v1/social/connections                     (read-only, no CSRF)
 *   POST /v1/social/connections/{id}/assign-domain  (session + CSRF)
 *
 * The pure view/model (types, row model, provider grouping, persisted:false
 * handling, error copy) lives beside this in data/social-connections-model.ts so
 * it can be unit-tested with no DOM (node --test) — this module only does I/O.
 * -------------------------------------------------------------------------- */

import { api, apiRequest } from '@/lib/api';
import {
  assignDomainBody,
  SOCIAL_CONNECTION_ENDPOINTS,
  type AssignDomainResult,
  type SocialConnectionDto,
} from './social-connections-model';

/**
 * GET /v1/social/connections — this workspace's connected social accounts.
 * Tolerant of both the enveloped `{ ok, connections:[...] }` (the live shape)
 * and a bare array, mirroring listConnections() in data/connections.ts.
 * Failures surface verbatim (503 social_meta_disabled / 401 / 403) — the caller
 * maps them to honest copy via listConnectionsErrorMessage / isSocialDisabled.
 */
export async function listSocialConnections(signal?: AbortSignal): Promise<SocialConnectionDto[]> {
  const res = await api.get<{ connections?: SocialConnectionDto[] } | SocialConnectionDto[]>(
    SOCIAL_CONNECTION_ENDPOINTS.list,
    undefined,
    signal,
  );
  if (Array.isArray(res)) return res;
  return res?.connections ?? [];
}

/**
 * POST /v1/social/connections/{id}/assign-domain — assign (projectId>0) or clear
 * (projectId=null) the SEO domain a connection belongs to. State-changing: the
 * CSRF double-submit header is attached automatically by apiRequest.
 *
 * IMPORTANT: a 200 with `persisted:false` is an HONEST no-op that happens BEFORE
 * the domain-scoping migration lands. Callers MUST route the result through
 * assignmentView() so a no-op is surfaced as still-Unassigned + a pending hint,
 * never as a successful assignment.
 */
export function assignConnectionDomain(
  id: number,
  projectId: number | null,
): Promise<AssignDomainResult> {
  return apiRequest<AssignDomainResult>(SOCIAL_CONNECTION_ENDPOINTS.assignDomain(id), {
    method: 'POST',
    body: assignDomainBody(projectId),
  });
}
